import logo from "@/assets/logo.png";
import { useAuthStore } from "@/stores/authStore";
import { getDefaultAdminRoute } from "@/routes/admin-route-registry";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowRight,
  BarChart3,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  ShieldCheck,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const loginSchema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(6, "Password should be at least 6 characters"),
});

type LoginForm = z.infer<typeof loginSchema>;

function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const completeMfaLogin = useAuthStore((state) => state.completeMfaLogin);
  const status = useAuthStore((state) => state.status);
  const [showPassword, setShowPassword] = useState(false);
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (values: LoginForm) => {
    try {
      if (mfaPending) {
        await completeMfaLogin(mfaCode.trim());
      } else {
        const result = await login(values.email, values.password);
        if (result.mfaRequired) {
          setMfaPending(true);
          toast("Enter the code from your authenticator app");
          return;
        }
      }
      toast.success("Welcome back, Administrator!");
      navigate(getDefaultAdminRoute(useAuthStore.getState().user?.role), {
        replace: true,
      });
    } catch (error) {
      toast.error("Unable to sign in. Check your credentials.");
    }
  };

  const isLoading = status === "loading";

  return (
    <div className="min-h-screen w-full lg:grid lg:grid-cols-2 bg-slate-950 relative overflow-hidden font-sans">
      {/* Decorative background element for the whole page */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[70%] h-[70%] rounded-full bg-blue-600/10 blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full bg-indigo-600/10 blur-[120px] animate-pulse delay-700" />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-soft-light" />
      </div>

      {/* Left Side - Immersive Hero Section (Desktop Only) */}
      <div className="hidden lg:flex flex-col justify-between p-16 relative overflow-hidden border-r border-white/5 bg-slate-950/50 backdrop-blur-3xl">
        {/* Hero Background Effects */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-[10%] left-[10%] w-[80%] h-[80%] rounded-full bg-blue-600/20 blur-[120px] animate-pulse" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] rounded-full bg-cyan-600/20 blur-[100px] animate-pulse delay-1000" />
        </div>

        {/* Branding */}
        <div className="relative z-10">
          <div className="flex items-center gap-4 bg-white/5 backdrop-blur-2xl py-4 px-7 rounded-[1.5rem] border border-white/10 shadow-2xl w-fit group hover:bg-white/10 transition-all duration-500">
            <div className="bg-white p-2 rounded-xl shadow-inner group-hover:scale-110 transition-transform duration-500">
              <img
                src={logo}
                alt="MediCorePay Logo"
                className="w-8 h-8 object-contain"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-2xl font-black tracking-tight leading-none text-white">
                MediCore<span className="text-blue-500">Pay</span>
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-blue-400 font-black mt-1">
                Control Center
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-lg space-y-12">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[10px] font-black uppercase tracking-widest">
              <span className="flex h-1.5 w-1.5 rounded-full bg-blue-400 animate-ping" />
              Platform Management v3.2
            </div>

            <div className="space-y-2">
              <h1 className="text-7xl font-black leading-[1.05] tracking-tighter text-white">
                Command <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                  Central
                </span>
              </h1>
            </div>

            <p className="text-lg text-slate-400 font-medium leading-relaxed max-w-md">
              Secure administrative access to the MediCorePay ecosystem. Monitor
              performance, manage tenants, and fine-tune system governance with
              elite precision.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6 pb-4">
            <div className="p-6 rounded-3xl bg-white/[0.03] border border-white/5 backdrop-blur-md hover:bg-white/[0.06] transition-all group">
              <div className="w-12 h-12 rounded-2xl bg-blue-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <ShieldCheck className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="font-black text-white text-xs uppercase tracking-wider">
                Enterprise RBAC
              </h3>
              <p className="text-[10px] text-slate-500 mt-2 font-medium">
                Granular permission matrix control
              </p>
            </div>
            <div className="p-6 rounded-3xl bg-white/[0.03] border border-white/5 backdrop-blur-md hover:bg-white/[0.06] transition-all group">
              <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <BarChart3 className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="font-black text-white text-xs uppercase tracking-wider">
                Real-time Data
              </h3>
              <p className="text-[10px] text-slate-500 mt-2 font-medium">
                Live analytics across all regions
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 flex justify-between items-center text-slate-500">
          <p className="text-xs font-bold tracking-widest uppercase">
            © 2026 MediCorePay.
          </p>
          <div className="flex gap-10">
            <a
              href="#"
              className="hover:text-white transition-colors text-[10px] font-black uppercase tracking-widest"
            >
              Protocol
            </a>
            <a
              href="#"
              className="hover:text-white transition-colors text-[10px] font-black uppercase tracking-widest"
            >
              Compliance
            </a>
          </div>
        </div>
      </div>

      {/* Right Side - Sign In Form */}
      <div className="flex flex-col items-center justify-center p-8 lg:p-12 relative bg-white">
        {/* Mobile-only background effects could go here, but the main ones are already present */}

        <div className="relative z-10 w-full max-w-[440px] animate-slide-up">
          <div className="bg-white rounded-[2.5rem] p-8 md:p-10 shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-slate-100">
            <div className="flex flex-col items-center mb-10 text-center">
              {/* Logo for mobile view */}
              <div className="lg:hidden relative mb-8 group transition-transform duration-500 hover:scale-105">
                <div className="absolute inset-0 bg-blue-500/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all" />
                <img
                  src={logo}
                  alt="MediCorePay"
                  className="relative h-14 w-auto object-contain drop-shadow-2xl"
                />
              </div>

              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-[10px] font-black uppercase tracking-widest mb-4">
                <span className="flex h-1.5 w-1.5 rounded-full bg-blue-500 animate-ping" />
                Secure Admin Gateway
              </div>

              <h1 className="text-4xl font-black text-slate-950 tracking-tight leading-tight">
                Admin Sign In
              </h1>
              <p className="text-slate-500 text-sm mt-3 font-medium">
                Enter your credentials to manage the platform
              </p>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                  Admin Email
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                    <Mail className="h-5 w-5" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    className="block w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:border-blue-500 transition-all duration-300 outline-none text-slate-950 placeholder-slate-400 font-bold text-sm"
                    {...register("email")}
                  />
                </div>
                {errors.email && (
                  <p className="text-[10px] text-red-500 font-bold ml-1 animate-slide-up flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-red-500" />
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between ml-1">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    Password
                  </label>
                </div>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                    <Lock className="h-5 w-5" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    className="block w-full pl-12 pr-12 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:border-blue-500 transition-all duration-300 outline-none text-slate-950 placeholder-slate-400 font-bold text-sm"
                    placeholder="••••••••"
                    {...register("password")}
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    onClick={() => setShowPassword((value) => !value)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-[10px] text-red-500 font-bold ml-1 animate-slide-up flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-red-500" />
                    {errors.password.message}
                  </p>
                )}
              </div>

              {mfaPending && (
                <div className="space-y-2 animate-slide-up">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                    Authenticator or recovery code
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-500 transition-colors">
                      <ShieldCheck className="h-5 w-5" />
                    </div>
                    <input
                      autoFocus
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={mfaCode}
                      onChange={(event) => setMfaCode(event.target.value)}
                      placeholder="123456"
                      className="block w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:border-blue-500 transition-all outline-none text-slate-950 font-bold tracking-[0.3em]"
                    />
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || (mfaPending && mfaCode.trim().length < 6)}
                className="w-full relative group py-4 px-6 bg-slate-950 text-white font-black text-sm rounded-2xl shadow-xl active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
              >
                <div className="absolute inset-0 bg-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="relative z-10 flex items-center justify-center gap-2 group-hover:text-white transition-colors">
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4" />
                      Authenticating...
                    </>
                  ) : (
                    <>
                      {mfaPending ? "Verify & sign in" : "Sign In"}
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1.5 transition-transform duration-300" />
                    </>
                  )}
                </span>
              </button>
            </form>

            <div className="mt-8 pt-8 border-t border-slate-100 text-center">
              <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">
                Authorized personnel only
              </p>
            </div>
          </div>

          <p className="mt-8 text-center text-slate-500 text-[10px] font-medium tracking-widest uppercase">
            © 2026 MediCorePay • System v3.2.0
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
